import '../styles/global.css';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { appWithTranslation } from 'next-i18next';
import nextI18nextConfig from '../next-i18next.config';
import { QueryProvider } from '@dx-ui/framework-react-query';
import { getQueryProviderProps } from '@dx-ui/framework-react-query-env-vars';
import { useRef } from 'react';
import type { AppContext, AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import type { CorpUserInfo } from '../lib';
import {
  CorpUserInfoProvider,
  MetricsProvider,
  SessionProvider,
  SessionContextTypes,
} from '../lib';
import type { DehydratedState } from '@tanstack/react-query';
import { getAuthClient } from '../utils/auth';
import type { ServerResponse } from 'http';
import { LocationProvider } from '@dx-ui/framework-location-provider';
import { logWarning, useManualPageLogger } from '@dx-ui/framework-logger';
import { isBrowser } from '@dx-ui/utilities-is-browser';
import { DatadogInit, useDatadogPageVisibility } from '@dx-ui/framework-datadog-rum';
import { env } from '@dx-ui/framework-env';

import { NextLinkProvider } from '@dx-ui/framework-link-context/next';
import { NextPagesRouterProvider } from '@dx-ui/framework-router-context/next';
import { BrandConfigProvider, getBrandConfig } from '@dx-ui/global-ui-prism';

const brandConfig = getBrandConfig('HI');

const SessionModal = dynamic(() => import('../components/SessionModal/SessionModal'));

const APP_NAME = 'dx-hiltonlink-ui';

type ExtendedServerResponse = ServerResponse & {
  locals?: {
    auth: {
      UserClaims: {
        firstName?: string;
        lastName?: string;
        emailAddress?: string;
        phoneNumber?: string;
      };
    };
  };
};

type IAppWrapperProps = {
  pageProps: {
    dehydratedState: DehydratedState;
  };
  corpUserInfo: CorpUserInfo;
} & AppProps;

function useCreateOnce<T>(fn: () => T) {
  const ref = useRef<T | null>(null);
  if (!ref.current) {
    ref.current = fn();
  }
  return ref.current;
}

const queryProps = getQueryProviderProps();

function App({ Component, pageProps, router, corpUserInfo }: IAppWrapperProps) {
  const corpUserContactInfo = useCreateOnce(() => corpUserInfo);
  const routerLocale = router.locale || 'en';
  useManualPageLogger(router.pathname, APP_NAME);
  useDatadogPageVisibility();

  return (
    <BrandConfigProvider brandConfig={brandConfig}>
      <DatadogInit
        applicationId={env('DATADOG_RUM_APPLICATION_ID')}
        clientToken={env('DATADOG_RUM_CLIENT_TOKEN')}
        service="dx-hiltonlink-ui"
        env={env('DATADOG_RUM_ENV') || env('APP_ENV')}
        version={env('APP_VER')}
        sessionSampleRate={
          env('DATADOG_RUM_SESSION_SAMPLE_RATE')
            ? Number(env('DATADOG_RUM_SESSION_SAMPLE_RATE'))
            : 100
        }
        sessionReplaySampleRate={
          env('DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE')
            ? Number(env('DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE'))
            : 0
        }
        applicationName={env('DATADOG_RUM_APP_NAME')}
        applicationUniqueId={env('DATADOG_RUM_APP_UNIQUE_ID')}
        allowedTracingUrlsPattern={env('DATADOG_RUM_ALLOWED_TRACING_URLS')}
      />
      <QueryProvider
        {...queryProps}
        dehydratedQueryState={pageProps.dehydratedState}
        appName="dx-hiltonlink-ui"
        routerLocale={router.locale || 'en'}
        oneLinkConfig={null}
        authenticatedOperationNames={[
          'guest',
          'brand_eventPageByRatePlan_hotel',
          'brand_eventRatePlan_hotel',
          'createEventPage',
          'updateEventPage',
          'publishEventPage',
        ]}
        handleAuthenticatedOperationError={async () => {
          await router.push(`/${router.locale}/my-event/sign-in/`);
          return null;
        }}
      >
        <SessionProvider contextType={SessionContextTypes.None}>
          <MetricsProvider>
            <LocationProvider>
              <NextLinkProvider>
                <NextPagesRouterProvider locale={routerLocale}>
                  <CorpUserInfoProvider corpUserInfo={corpUserContactInfo}>
                    <SessionModal />
                    <Component {...pageProps} router={router} />
                  </CorpUserInfoProvider>
                </NextPagesRouterProvider>
              </NextLinkProvider>
            </LocationProvider>
          </MetricsProvider>
        </SessionProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryProvider>
    </BrandConfigProvider>
  );
}

App.getInitialProps = async (appContext: AppContext) => {
  const { Component, ctx, router } = appContext;
  const res = ctx.res as ExtendedServerResponse;
  const authClient = getAuthClient(ctx);
  // Wait for user to resolve
  await authClient.getGuestInfo()?.catch((e) => {
    logWarning('FETCH_ERROR', e as Error, 'authClient.getGuestInfo');
  });
  const isCorpUser = authClient.isCorpUser();
  const corpUserInfo = { firstName: '', lastName: '', emailAddress: '', phoneNumber: '' };
  const isAuthenticated = authClient.getIsLoggedIn();

  let pageProps: Partial<IAppWrapperProps> = { router };

  if (!isBrowser) {
    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps(ctx);
    }

    if (isAuthenticated && isCorpUser && res && res.locals?.auth) {
      corpUserInfo.firstName = res.locals.auth.UserClaims?.firstName || '';
      corpUserInfo.lastName = res.locals.auth.UserClaims?.lastName || '';
      corpUserInfo.emailAddress = res.locals.auth.UserClaims?.emailAddress || '';
      corpUserInfo.phoneNumber = res.locals.auth.UserClaims?.phoneNumber || '';
    }
  }

  return {
    pageProps,
    corpUserInfo,
  };
};

export default appWithTranslation<IAppWrapperProps>(App, nextI18nextConfig);
